import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Minus, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { DialogHeaderBlock } from "@/components/patterns/headers";
import {
      Dialog,
      DialogContent,
      DialogFooter,
} from "@/components/ui/dialog";
import { PROMPT_TYPES } from "@/lib/constants/prompts";
import { getPromptEditorAutofillProps } from "@/lib/prompt-input";
import { useTRPC } from "@/lib/trpc";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

interface CreatePromptsDialogProps {
      isOpen: boolean;
      searchSpecificationId: string;
      setIsOpen: (isOpen: boolean) => void;
}

export function CreatePromptsDialog({
      isOpen,
      setIsOpen,
      searchSpecificationId,
}: CreatePromptsDialogProps) {
      const trpc = useTRPC();
      const [prompts, setPrompts] = useState<
            {
                  text: string;
                  language?: string;
                  promptType: "organic" | "competitor" | "brand_specific";
            }[]
      >([{ text: "", promptType: "organic" }]);

      const addPrompt = () => {
            setPrompts([...prompts, { text: "", promptType: "organic" }]);
      };

      const queryClient = useQueryClient();
      const invalidatePromptQueries = () => {
            queryClient.invalidateQueries({ queryKey: ["prompts"] });
            queryClient.invalidateQueries({ queryKey: trpc.prompts.getSearchSpecificationPromptList.queryKey() });
            queryClient.invalidateQueries({ queryKey: trpc.prompts.getSearchSpecificationPromptMetrics.queryKey() });
            queryClient.invalidateQueries({ queryKey: trpc.prompts.getSearchSpecificationPrompts.queryKey() });
            queryClient.invalidateQueries({
                  queryKey: trpc.searchSpecifications.getSearchSpecificationPromptsCount.queryKey({
                        id: searchSpecificationId,
                  }),
            });
      };

      const createPromptsMutation = useMutation(
            trpc.prompts.createPrompts.mutationOptions({
                  onSuccess: () => {
                        toast.success("Prompts saved successfully");
                        invalidatePromptQueries();
                        setIsOpen(false);
                        setPrompts([{ text: "", promptType: "organic" }]);
                  },
            }),
      );

      const removePrompt = (index: number) => {
            if (prompts.length > 1) {
                  setPrompts(prompts.filter((_, i) => i !== index));
            }
      };

      const updatePrompt = (
            index: number,
            value: {
                  text: string;
                  language?: string;
                  promptType: "organic" | "competitor" | "brand_specific";
            },
      ) => {
            const newPrompts = [...prompts];
            newPrompts[index] = value;
            setPrompts(newPrompts);
      };

      return (
            <Dialog open={isOpen} modal onOpenChange={setIsOpen}>
                  <DialogContent className="sm:max-w-[650px]">
                        <DialogHeaderBlock title="Create Prompts" />
                        <div className="flex flex-col gap-3 py-1">
                              {prompts.map((prompt, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                          <Input
                                                {...getPromptEditorAutofillProps(`manual-prompt-${index + 1}`)}
                                                placeholder="Enter prompt text..."
                                                value={prompt.text}
                                                onChange={(e) => updatePrompt(index, { ...prompt, text: e.target.value })}
                                                variant="brand"
                                                className="flex-1"
                                          />
                                          <Select
                                                value={prompt.promptType}
                                                onValueChange={(value) =>
                                                      updatePrompt(index, {
                                                            ...prompt,
                                                            promptType: value as "organic" | "competitor" | "brand_specific",
                                                      })
                                                }
                                          >
                                                <SelectTrigger variant="brand" size="sm" className="w-28">
                                                      <SelectValue placeholder="Type" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                      {PROMPT_TYPES.map((type) => (
                                                            <SelectItem key={type.value} value={type.value || ""}>
                                                                  {type.label}
                                                            </SelectItem>
                                                      ))}
                                                </SelectContent>
                                          </Select>
                                          <Button
                                                variant="brandOutline"
                                                size="iconSm"
                                                className="text-brand-ink/50 hover:text-brand-ink"
                                                onClick={() => removePrompt(index)}
                                                disabled={prompts.length === 1}
                                          >
                                                <Minus className="h-4 w-4" />
                                          </Button>
                                    </div>
                              ))}
                              <Button
                                    variant="brandDashed"
                                    onClick={addPrompt}
                                    className="h-10 w-full"
                              >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Prompt
                              </Button>
                        </div>
                        <DialogFooter className="gap-2">
                              <Button
                                    variant="brandOutline"
                                    className="mr-auto"
                                    onClick={() => {
                                          setIsOpen(false);
                                          setPrompts([{ text: "", promptType: "organic" }]);
                                    }}
                              >
                                    Cancel
                              </Button>
                              <Button
                                    variant="brand"
                                    onClick={() => {
                                          createPromptsMutation.mutate({
                                                prompts: prompts.map((p) => ({
                                                      text: p.text,
                                                      promptType: p.promptType.toUpperCase().replace(/_/g, "_") as
                                                            | "ORGANIC"
                                                            | "COMPETITOR"
                                                            | "BRAND_SPECIFIC",
                                                      searchSpecificationId,
                                                      status: "ACTIVE" as const,
                                                })),
                                          });
                                    }}
                              >
                                    Create Prompts
                              </Button>
                        </DialogFooter>
                  </DialogContent>
            </Dialog>
      );
}
